import React from 'react';

import { Info as InfoIcon } from '@mui/icons-material';
import { Alert, Box, Button, Typography } from '@mui/material';
import { FORM_ERROR } from 'final-form';

import { Field, Form } from '@hrlogics/react-components/form';

import { PIN_MASK } from '@clearverify/constants';
import type { Nullable } from '@clearverify/types';

import { Tooltip } from 'src/components/common';
import { MaskedTextField } from 'src/components/finalForm';
import { FORM_ERROR_KEY, PinFormField } from 'src/constants/signup';
import type { SxProp } from 'src/types/mui';
import type { PinFormValues } from 'src/types/signup';

import { pinFormValidationSchema } from '../utils';

export type PinFormProps = {
  onSubmit: (formValues: PinFormValues) => void;
  companyName: Nullable<string>;
  customHint: Nullable<string>;
};

export const PinForm: React.FC<PinFormProps> = ({ onSubmit, companyName, customHint }) => {
  const pinHint =
    customHint ||
    `Please reach out to your employer for your 8 digit PIN that can be used to sign up for an account with Clear Verify.`;

  return (
    <Box>
      <Box display="flex" alignItems="center">
        <Typography variant="body2" fontSize="24px">
          You've been invited by <b>{companyName}</b> to create your account at Clear Verify
        </Typography>
      </Box>

      <Form<PinFormValues>
        onSubmit={onSubmit}
        subscription={{ submitting: true, submitErrors: true }}
        containerProps={{ mt: 4 }}
        validationSchema={pinFormValidationSchema}
      >
        {({ submitting, submitErrors }) => {
          return (
            <React.Fragment>
              <Box display="flex" gap={0.5}>
                <Typography variant="inputLabel" fontWeight="600" fontSize="16px">
                  Employer PIN
                </Typography>
                <Tooltip title={pinHint} placement="right" arrow>
                  <InfoIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                </Tooltip>
              </Box>

              <Box mt={1.5}>
                <Field
                  name={PinFormField.pin}
                  component={MaskedTextField}
                  label="Employer PIN"
                  mask={PIN_MASK}
                  disabled={submitting}
                  fullWidth
                  required
                />
              </Box>

              {submitErrors?.[FORM_ERROR] === FORM_ERROR_KEY.PIN_NOT_FOUND && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  PIN not found. Please check your PIN and try again.
                </Alert>
              )}

              <Box display="flex" justifyContent="center" mt={4}>
                <Button
                  type="submit"
                  sx={submitButtonStyles}
                  disabled={submitting}
                  loading={submitting}
                  size="large"
                  variant="contained"
                >
                  Next step
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};
